import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import AutoSizer from 'react-virtualized-auto-sizer';

import { app, media } from 'context';

import HeaderRow from './HeaderRow';
import Row from './Row';
import TableControlsHeader from './TableControlsHeader';
import TableFooter from './TableFooter';
import { CELL_HEIGHT } from './constants';
import {
  Column,
  ColumnWidths,
  DefaultFilterState,
  DefaultSortState,
  FilterState,
  Pagination,
  Row as RowType,
  SetColumnWidths,
  SortState
} from './types';
import { filterRows } from './utils';
import Button from '../Button';
import IconButton from '../IconButton';
import Layout from '../Layout';
import { TABLE_FILTER_SORT_HEIGHT_MOBILE, TABLE_TITLE_HEIGHT_MOBILE } from '../PageLayout/constants';
import { sortRows } from '../Table/utils';
import Text from '../Text';
import { BOTTOM_NAVIGATION_HEIGHT, NEW_TABLE_HEADER_HEIGHT } from '../constants';
import { lockScreenScroll, unlockScreenScroll } from '../utils';

export interface Props<T extends RowType> {
  nonPaginatedRows?: T[];
  columns: Column<T>[];
  rowHeight?: number;
  headerRowCount?: number;
  defaultSortState?: DefaultSortState<T>[];
  defaultFilterState?: DefaultFilterState<T>[];
  minWidth?: number;
  title?: string;
  newPath?: string;
  newOnClick?: VoidFunction;
  CTA?: JSX.Element;
  rowOnClick?: (row: T, e: React.MouseEvent) => void;
  rowHref?: (row: T) => string;
  height?: number;
  type?: 'simple' | 'default';
  LeftMobileElement?: JSX.Element;
  paddingLeft?: number;
  paddingRight?: number;
  disableTableControlsHeader?: boolean;
  pagination?: Pagination;
}

const NewTable = <T extends RowType>({
  columns,
  headerRowCount = 1,
  defaultSortState = [],
  defaultFilterState = [],
  minWidth,
  rowHeight = CELL_HEIGHT,
  newPath,
  newOnClick,
  title,
  CTA,
  rowOnClick,
  rowHref,
  type = 'default',
  disableTableControlsHeader = false,
  pagination,
  nonPaginatedRows,
  ...props
}: Props<T>) => {
  const history = useHistory();
  const columnWidthsRef = useRef<ColumnWidths>({});
  const { windowHeight, appPaddingX } = useContext(app);
  const paddingLeft = props.paddingLeft ?? appPaddingX;
  const paddingRight = props.paddingRight ?? appPaddingX;
  const [renderOrdinal, setRenderOrdinal] = useState(0);
  const [backendRows, setBackendRows] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { xs, sm, lg } = useContext(media);
  const isMobile = xs || sm;
  const { sort, filters } = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  const sortParams: SortState[] = sort ? (JSON.parse(sort as string) as SortState[]) : [];
  const filterParams: FilterState[] = filters
    ? (JSON.parse(filters as string) as FilterState[]).map(filter => {
        if (filter.endDate) {
          filter.endDate = new Date(filter.endDate);
        }
        if (filter.startDate) {
          filter.startDate = new Date(filter.startDate);
        }
        return filter;
      })
    : [];
  const [sortState, setSortState] = useState<SortState[]>(
    sortParams.length > 0 ? sortParams : (defaultSortState as SortState[])
  );
  const [filterState, setFilterState] = useState<FilterState[]>(
    filterParams.length > 0 ? filterParams : (defaultFilterState as FilterState[])
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination?.defaultPageSize ?? 10);

  // Effect to handle initial data fetch when pagination is enabled
  useEffect(() => {
    const fetchInitialData = async () => {
      if (pagination) {
        const data = await pagination.onFilterAndSortStateChange?.({
          filterState,
          sortState,
          page: 1,
          pageSize
        });
        if (data) {
          setTotalCount(data.total);
          setBackendRows(data.items);
        }
      }
    };
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [tableHeight, setTableHeight] = useState<number>();

  // Filter out hidden columns only for rendering
  const visibleColumns = columns.filter(column => !column.hide);

  const rows = sortRows({
    rows: filterRows({ rows: nonPaginatedRows || [], filterState, columns }),
    columns,
    sortState
  });

  useEffect(() => {
    if (type === 'simple') return;

    if (xs) {
      lockScreenScroll();
    }

    return () => {
      if (xs) {
        unlockScreenScroll();
      }
    };
  }, [xs, type]);

  const setColumnWidths: SetColumnWidths = columnWidths => {
    columnWidthsRef.current = { ...columnWidthsRef.current, ...columnWidths };

    // if all column widths are present, increment renderOrdinal
    if (Object.values(columnWidthsRef.current).every(v => v !== undefined) && renderOrdinal === 0) {
      setRenderOrdinal(1);
    }
  };

  const handleSetFilterState = async (filterState: FilterState[]) => {
    setFilterState(filterState);
    setCurrentPage(1); // Reset to first page
    const filterParams = new URLSearchParams({ filters: JSON.stringify(filterState) });
    const searchParams = new URLSearchParams({ sort: JSON.stringify(sortState) });
    history.replace({
      pathname: history.location.pathname,
      search: `${filterParams.toString()}&${searchParams.toString()}`
    });

    if (pagination) {
      const data = await pagination.onFilterAndSortStateChange?.({
        filterState,
        sortState,
        page: 1,
        pageSize
      });
      if (data) {
        setTotalCount(data.total);
        setBackendRows(data.items);
      }
    }
  };

  const handleSetSortState = async (sortState: SortState[]) => {
    setSortState(sortState);
    setCurrentPage(1); // Reset to first page
    const filterParams = new URLSearchParams({ filters: JSON.stringify(filterState) });
    const searchParams = new URLSearchParams({ sort: JSON.stringify(sortState) });
    history.replace({
      pathname: history.location.pathname,
      search: `${filterParams.toString()}&${searchParams.toString()}`
    });

    if (pagination) {
      const data = await pagination.onFilterAndSortStateChange?.({
        filterState,
        sortState,
        page: 1,
        pageSize
      });
      if (data) {
        setTotalCount(data.total);
        setBackendRows(data.items);
      }
    }
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    const data = await pagination?.onPageSizeChange?.({
      filterState,
      sortState,
      page: 1,
      pageSize: newPageSize
    });
    if (data) {
      setTotalCount(data.total);
      setBackendRows(data.items);
    }
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);
    if (pagination) {
      const data = await pagination.onPageChange?.({
        filterState,
        sortState,
        page: newPage,
        pageSize
      });
      if (data) {
        setTotalCount(data.total);
        setBackendRows(data.items);
      }
    }
  };

  const columnWidths = columns.reduce<ColumnWidths>((widths, column) => {
    widths[column.key as string] = columnWidthsRef.current[column.key as string];

    return widths;
  }, {});

  const totalColumnWidth = Object.values(columnWidths).reduce((sum, width) => (sum || 0) + (width || 0), 0);

  const isDefaultType = type === 'default';
  const calculatedMinWidth = columns.every(c => c.width && typeof c.width === 'number')
    ? columns.reduce((sum, c) => sum + Number(c.width), 0)
    : undefined;

  const headerIndices = new Array(headerRowCount).fill(0).map((_, index) => index);

  const getSearchInputColumn = () => {
    const textSearchColumnSelected = filterState.find(filter => filter.equals);

    return textSearchColumnSelected?.key;
  };

  return (
    <Layout flexGrow={1} overflow="hidden">
      {isDefaultType && (!!title || !!newPath || !!newOnClick || !!CTA) && (
        <Layout
          paddingX={paddingLeft}
          align="center"
          justify="space-between"
          {...(isMobile ? { height: TABLE_TITLE_HEIGHT_MOBILE } : { paddingTop: 24, paddingBottom: 16 })}
        >
          <Layout align="center" height={44}>
            {title && (
              <Text size="h5" ellipsis>
                {title}
              </Text>
            )}
          </Layout>
          <Layout gap={8} align="center">
            {CTA}
            {(newPath || newOnClick) &&
              (isMobile ? (
                <IconButton
                  size="lg"
                  name="plus"
                  type="noBackground"
                  onClick={
                    newOnClick ||
                    (() => {
                      window.open(newPath, '_blank');
                    })
                  }
                />
              ) : (
                <Button
                  type="primary"
                  href={newPath}
                  size="xxs"
                  icon="plus"
                  iconPosition="left"
                  onClick={newOnClick}
                  text="Add"
                />
              ))}
          </Layout>
        </Layout>
      )}
      <Layout
        {...(isDefaultType ? { marginTop: isMobile ? undefined : 12, paddingLeft: paddingLeft } : {})}
        onMeasure={({ y }) => {
          if (type === 'simple') {
            setTableHeight(props.height || y);
          } else {
            setTableHeight(windowHeight - y - (isMobile ? BOTTOM_NAVIGATION_HEIGHT + 12 : 0));
          }
        }}
      />
      {tableHeight && (
        <Layout
          {...(xs
            ? {
                height: `calc(100dvh - ${
                  TABLE_TITLE_HEIGHT_MOBILE + TABLE_FILTER_SORT_HEIGHT_MOBILE + BOTTOM_NAVIGATION_HEIGHT
                }px)`
              }
            : { height: tableHeight })}
          {...(isDefaultType ? { paddingLeft: paddingLeft, paddingRight: paddingRight } : {})}
          flex
        >
          <AutoSizer
            onResize={() => {
              const resetWidths = columns.reduce<ColumnWidths>((widths, column) => {
                widths[column.key as string] = undefined;
                return widths;
              }, {});
              setColumnWidths(resetWidths);
              setRenderOrdinal(0);
            }}
          >
            {({ width, height }) => (
              <>
                {!disableTableControlsHeader && (
                  <TableControlsHeader
                    width={!lg ? width : totalColumnWidth}
                    columns={columns as any}
                    rows={pagination ? backendRows : rows}
                    filterState={filterState}
                    sortState={sortState}
                    setFilterState={handleSetFilterState}
                    removeFilter={({ equals }) => {
                      let updatedFilterState = [...filterState];
                      const key = getSearchInputColumn();

                      if (key) {
                        if (equals === '') {
                          updatedFilterState = updatedFilterState.filter(filter => filter.key !== key);
                        }

                        handleSetFilterState(updatedFilterState);
                      }
                    }}
                  />
                )}
                {/* the padding bottom value should below should technically be set to the height of the controls header + the footer height*/}
                <div style={{ width, height, minWidth, overflow: 'auto', paddingBottom: 162 }}>
                  {headerIndices.map(index => (
                    <HeaderRow
                      disableTableControlsHeader={disableTableControlsHeader}
                      getColumnWidth={(key: string) => {
                        return columnWidthsRef.current[key];
                      }}
                      setColumnWidths={setColumnWidths!}
                      columns={visibleColumns}
                      key={index}
                      sortState={sortState!}
                      setSortState={handleSetSortState}
                      filterState={filterState!}
                      setFilterState={handleSetFilterState}
                      style={{
                        left: 0,
                        width: !lg ? totalColumnWidth : '100%',
                        height: NEW_TABLE_HEADER_HEIGHT
                      }}
                    />
                  ))}
                  {(pagination && backendRows ? backendRows : rows || []).map((row, index) => (
                    <Row
                      key={`${row.id}-${renderOrdinal}`}
                      style={{ left: 0, width: !lg ? totalColumnWidth : '100%', height: rowHeight }}
                      data={{ columns: visibleColumns, row }}
                      index={index}
                      getColumnWidth={(key: string) => {
                        return columnWidthsRef.current[key];
                      }}
                      minWidth={minWidth || calculatedMinWidth}
                      rowOnClick={rowOnClick}
                      rowHref={rowHref}
                    />
                  ))}
                  <TableFooter
                    pageSize={pageSize}
                    handlePageSizeChange={handlePageSizeChange}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    rows={pagination ? backendRows : rows}
                    pagination={pagination}
                    totalCount={totalCount}
                    width={!lg ? totalColumnWidth : undefined}
                  />
                </div>
              </>
            )}
          </AutoSizer>
        </Layout>
      )}
    </Layout>
  );
};

export default NewTable;
